import { generateId } from "@tennis-matches/core/utils";
import { getSupabaseClient } from "../supabase/index.js";
import { isTrainerstundenBillingMissingColumnError } from "./billing-statement-db-compat.js";
import { snapshotBillingStatementDraftMasterData } from "./billing-statement-invoice-freeze.js";

function isDuplicateSessionPersonError(err) {
  const raw =
    err && typeof err === "object" ? err.message || err.details || "" : err;
  const msg = String(raw ?? "").toLowerCase();
  return (
    msg.includes("trainerstunden_billing_duplicate_session_person_active") ||
    msg.includes("trainerstunden_billing_reactivate_statement_item_conflict")
  );
}

function nz(v) {
  return String(v ?? "").trim();
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function buildStatementsById(statements) {
  const byId = new Map();
  for (const st of Array.isArray(statements) ? statements : []) {
    const id = nz(st?.id);
    if (id) byId.set(id, st);
  }
  return byId;
}

function isSplinterDescendantOfStatement(targetId, ancestorId, byId) {
  let cur = byId.get(nz(targetId));
  const visited = new Set();
  while (cur) {
    const parentId = nz(cur.split_from_statement_id);
    if (!parentId) return false;
    if (parentId === nz(ancestorId)) return true;
    if (visited.has(parentId)) return false;
    visited.add(parentId);
    cur = byId.get(parentId);
  }
  return false;
}

/**
 * Liegen zwei Entwürfe in derselben Split-Kaskade (Vorfahr/Nachfahr)?
 */
export function areBillingStatementsInSameSplitTree(
  statementIdA,
  statementIdB,
  statements = [],
) {
  const aId = nz(statementIdA);
  const bId = nz(statementIdB);
  if (!aId || !bId) return false;
  if (aId === bId) return true;
  const byId = buildStatementsById(statements);
  return (
    isSplinterDescendantOfStatement(aId, bId, byId) ||
    isSplinterDescendantOfStatement(bId, aId, byId)
  );
}

/**
 * Ausgelagerte Entwürfe (Nachfahren) eines Quell-Entwurfs — älteste zuerst.
 */
export function collectBillingStatementSplinterDraftTargets(
  sourceStatementId,
  statements = [],
) {
  const sourceId = nz(sourceStatementId);
  if (!sourceId) return [];
  const byId = buildStatementsById(statements);

  return (Array.isArray(statements) ? statements : [])
    .filter((st) => {
      if (nz(st?.status) !== "draft") return false;
      if (nz(st?.id) === sourceId) return false;
      return isSplinterDescendantOfStatement(nz(st?.id), sourceId, byId);
    })
    .sort((a, b) => {
      const ta = Date.parse(String(a?.created_at || "")) || 0;
      const tb = Date.parse(String(b?.created_at || "")) || 0;
      return ta - tb || nz(a?.id).localeCompare(nz(b?.id));
    });
}

/** @param {object | null | undefined} statement */
export function isBillingStatementDraftMergeBlocked(statement) {
  return Boolean(statement?.draft_merge_blocked);
}

async function recalcStatementTotals(supabase, statementId) {
  const sid = nz(statementId);
  const { data: items, error: itemsErr } = await supabase
    .from("trainerstunden_billing_statement_items")
    .select("amount_eur")
    .eq("statement_id", sid);
  if (itemsErr) return { ok: false, error: itemsErr };
  const total = round2(
    (Array.isArray(items) ? items : []).reduce(
      (sum, it) => sum + (Number(it?.amount_eur) || 0),
      0,
    ),
  );
  const { data, error } = await supabase
    .from("trainerstunden_billing_statements")
    .update({ total_eur: total, open_eur: total, payment_reference: null })
    .eq("id", sid)
    .eq("status", "draft")
    .select()
    .single();
  if (error) return { ok: false, error };
  return { ok: true, data, itemCount: (items || []).length };
}

function buildSplinterHeaderFromSource(source, newId) {
  const src = source && typeof source === "object" ? source : {};
  return {
    id: newId,
    tenant_id: src.tenant_id ?? null,
    created_by: src.created_by ?? null,
    period_month: nz(src.period_month),
    recipient_type: nz(src.recipient_type),
    person_id: nz(src.person_id) || null,
    billing_contact_id: nz(src.billing_contact_id) || null,
    recipient_name_snapshot: src.recipient_name_snapshot ?? null,
    recipient_email_snapshot: src.recipient_email_snapshot ?? null,
    recipient_phone_snapshot: src.recipient_phone_snapshot ?? null,
    status: "draft",
    total_eur: 0,
    paid_eur: 0,
    open_eur: 0,
    due_date: src.due_date ?? null,
    draft_issuer_trainer_id: nz(src.draft_issuer_trainer_id) || null,
    split_from_statement_id: nz(src.id),
    draft_merge_blocked: true,
  };
}

function resolveRemoveErrorMessage(error) {
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    return String(error.message || error.details || error.hint || "");
  }
  return String(error ?? "");
}

export function resolveBillingDraftLineRemoveErrorKey(error) {
  const code = String(
    error && typeof error === "object" ? error.code || "" : "",
  ).trim();
  const msg = resolveRemoveErrorMessage(error).toLowerCase();

  if (
    error === "duplicate-session-person-active" ||
    msg.includes("trainerstunden_billing_duplicate_session_person_active")
  ) {
    return "trainerstunden.billing.correction.removeDuplicateSession";
  }
  if (
    error === "not-draft" ||
    error === "target-not-draft" ||
    msg.includes("trainerstunden_billing_statement_item_immutable")
  ) {
    return "trainerstunden.billing.correction.removeNotDraft";
  }
  if (error === "items-not-in-statement" || error === "item-not-found") {
    return "trainerstunden.billing.correction.removeItemNotFound";
  }
  if (msg.includes("created_by required for billing guard")) {
    return "trainerstunden.billing.correction.removeMissingOwner";
  }
  if (code === "42501" || msg.includes("permission") || msg.includes("rls")) {
    return "trainerstunden.billing.correction.removeForbidden";
  }
  return "trainerstunden.billing.correction.removeFailed";
}

/**
 * @param {unknown} error
 * @returns {string} i18n key
 */
export function resolveBillingDraftMergeErrorKey(error) {
  const nested =
    error && typeof error === "object" && error.error != null
      ? error.error
      : error;
  const reason =
    nested && typeof nested === "object"
      ? String(nested.reason || nested.code || nested.message || "").trim()
      : String(nested ?? "").trim();
  const code = String(
    nested && typeof nested === "object" ? nested.code || "" : "",
  ).trim();
  const msg = resolveRemoveErrorMessage(nested).toLowerCase();
  const token = reason || msg;

  if (
    nested === "duplicate-session-person-active" ||
    token === "duplicate-session-person-active" ||
    msg.includes("trainerstunden_billing_duplicate_session_person_active")
  ) {
    return "trainerstunden.billing.monthClose.mergeDuplicateSession";
  }
  if (nested === "need-multiple" || token === "need-multiple") {
    return "trainerstunden.billing.monthClose.mergeNeedMultiple";
  }
  if (nested === "recipient-mismatch" || token === "recipient-mismatch") {
    return "trainerstunden.billing.monthClose.mergeRecipientMismatch";
  }
  if (
    nested === "not-draft" ||
    token === "not-draft" ||
    msg.includes("trainerstunden_billing_statement_item_immutable")
  ) {
    return "trainerstunden.billing.monthClose.mergeNotDraft";
  }
  if (
    nested === "merge-blocked-splinter" ||
    nested === "split-tree-conflict" ||
    token === "merge-blocked-splinter" ||
    token === "split-tree-conflict"
  ) {
    return "trainerstunden.billing.monthClose.mergeSplitConflict";
  }
  if (nested === "statement-not-found" || token === "statement-not-found") {
    return "trainerstunden.billing.monthClose.mergeStatementNotFound";
  }
  if (msg.includes("created_by required for billing guard")) {
    return "trainerstunden.billing.monthClose.mergeMissingOwner";
  }
  if (code === "42501" || msg.includes("permission") || msg.includes("rls")) {
    return "trainerstunden.billing.monthClose.mergeForbidden";
  }
  return "trainerstunden.billing.monthClose.mergeSelectedFailed";
}

/**
 * Positionen aus Entwurf auslagern (neuer Splinter-Entwurf oder bestehender Ziel-Entwurf).
 */
export async function spinOffBillingStatementDraftLines(input = {}) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const sourceId = nz(input.sourceStatementId);
    const itemIds = [
      ...new Set(
        (Array.isArray(input.itemIds) ? input.itemIds : [])
          .map((id) => nz(id))
          .filter(Boolean),
      ),
    ];
    const targetSplinterId = nz(input.targetSplinterStatementId);
    if (!sourceId || !itemIds.length)
      return { ok: false, error: "missing-input" };

    const { data: source, error: sourceErr } = await supabase
      .from("trainerstunden_billing_statements")
      .select("*")
      .eq("id", sourceId)
      .single();
    if (sourceErr) return { ok: false, error: sourceErr };
    if (nz(source?.status) !== "draft")
      return { ok: false, error: "not-draft" };

    const { data: toMove, error: selErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .select("id,amount_eur,created_by")
      .eq("statement_id", sourceId)
      .in("id", itemIds);
    if (selErr) return { ok: false, error: selErr };
    const moveRows = Array.isArray(toMove) ? toMove : [];
    const moveIds = moveRows.map((it) => nz(it.id)).filter(Boolean);
    if (!moveIds.length) return { ok: false, error: "items-not-in-statement" };

    const sourceOwnerId = nz(source?.created_by);
    const missingOwnerIds = moveRows
      .filter((it) => !nz(it?.created_by) && sourceOwnerId)
      .map((it) => nz(it.id))
      .filter(Boolean);
    if (missingOwnerIds.length) {
      const { error: ownerErr } = await supabase
        .from("trainerstunden_billing_statement_items")
        .update({ created_by: source.created_by })
        .eq("statement_id", sourceId)
        .in("id", missingOwnerIds);
      if (ownerErr) return { ok: false, error: ownerErr };
    }

    let targetId = targetSplinterId;
    let createdSplinter = false;

    if (targetId) {
      const { data: target, error: targetErr } = await supabase
        .from("trainerstunden_billing_statements")
        .select("*")
        .eq("id", targetId)
        .single();
      if (targetErr) return { ok: false, error: targetErr };
      if (nz(target?.status) !== "draft")
        return { ok: false, error: "target-not-draft" };
      if (!isBillingStatementDraftMergeBlocked(target)) {
        return { ok: false, error: "target-not-splinter" };
      }
      let relatedQuery = supabase
        .from("trainerstunden_billing_statements")
        .select("*")
        .eq("status", "draft")
        .eq("recipient_type", nz(source.recipient_type));
      if (nz(source.recipient_type) === "contact") {
        relatedQuery = relatedQuery.eq(
          "billing_contact_id",
          nz(source.billing_contact_id),
        );
      } else {
        relatedQuery = relatedQuery.eq("person_id", nz(source.person_id));
      }
      const { data: relatedDrafts, error: relatedErr } = await relatedQuery;
      if (relatedErr) return { ok: false, error: relatedErr };
      const byId = buildStatementsById([
        source,
        target,
        ...(Array.isArray(relatedDrafts) ? relatedDrafts : []),
      ]);
      if (!isSplinterDescendantOfStatement(targetId, sourceId, byId)) {
        return { ok: false, error: "invalid-splinter-target" };
      }
    } else {
      targetId = generateId(12);
      let headerPayload = buildSplinterHeaderFromSource(source, targetId);
      let { data: inserted, error: insErr } = await supabase
        .from("trainerstunden_billing_statements")
        .insert([headerPayload])
        .select()
        .single();
      if (insErr && isTrainerstundenBillingMissingColumnError(insErr)) {
        const {
          split_from_statement_id: _a,
          draft_merge_blocked: _b,
          ...rest
        } = headerPayload;
        headerPayload = rest;
        ({ data: inserted, error: insErr } = await supabase
          .from("trainerstunden_billing_statements")
          .insert([headerPayload])
          .select()
          .single());
      }
      if (insErr) return { ok: false, error: insErr };
      targetId = nz(inserted?.id) || targetId;
      createdSplinter = true;
    }

    const { error: moveErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .update({ statement_id: targetId })
      .eq("statement_id", sourceId)
      .in("id", moveIds);
    if (moveErr) {
      if (createdSplinter) {
        await supabase
          .from("trainerstunden_billing_statements")
          .delete()
          .eq("id", targetId);
      }
      if (isDuplicateSessionPersonError(moveErr)) {
        return {
          ok: false,
          error: "duplicate-session-person-active",
          details: moveErr,
        };
      }
      return { ok: false, error: moveErr };
    }

    const sourceRecalc = await recalcStatementTotals(supabase, sourceId);
    if (!sourceRecalc.ok) return sourceRecalc;

    const targetRecalc = await recalcStatementTotals(supabase, targetId);
    if (!targetRecalc.ok) return targetRecalc;

    let deletedSource = false;
    if ((sourceRecalc.itemCount || 0) === 0) {
      const { error: delErr } = await supabase
        .from("trainerstunden_billing_statements")
        .delete()
        .eq("id", sourceId)
        .eq("status", "draft");
      if (delErr) return { ok: false, error: delErr };
      deletedSource = true;
    } else {
      await snapshotBillingStatementDraftMasterData(supabase, sourceId);
    }

    await snapshotBillingStatementDraftMasterData(supabase, targetId);

    return {
      ok: true,
      sourceStatementId: sourceId,
      targetStatementId: targetId,
      createdSplinter,
      deletedSource,
      movedCount: moveIds.length,
      source: deletedSource ? null : sourceRecalc.data,
      target: targetRecalc.data,
    };
  } catch (e) {
    console.error("[spinOffBillingStatementDraftLines] Exception:", e);
    return { ok: false, error: e };
  }
}
